// frontend/src/utils/conversation/turnAttachmentTracker.js

function normalizeText(value = '') {
  return String(value).toLowerCase().trim();
}

function wordCount(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

function isQuestion(text = '') {
  const normalized = normalizeText(text);

  if (!normalized) return false;
  if (normalized.endsWith('?')) return true;

  const starters = [
    'who','what','when','where','why','how',
    'do','does','did','is','are','was','were',
    'can','could','would','will','should','am',
  ];

  return starters.some((starter) => normalized.startsWith(`${starter} `));
}

function containsPronoun(text = '') {
  const normalized = normalizeText(text);

  const pronouns = [
    'he','she','they','them','him','her',
    'that','those','it','one',
  ];

  return pronouns.some((p) => new RegExp(`\\b${p}\\b`, 'i').test(normalized));
}

function isAgreement(text = '') {
  const normalized = normalizeText(text);

  const phrases = [
    'yes','yeah','yep','right','correct','exactly',
    'true',"that's right",
  ];

  return phrases.some((p) => normalized === p || normalized.startsWith(`${p} `));
}

function isSoftClarification(text = '') {
  const normalized = normalizeText(text);

  const phrases = [
    'sort of','kind of','a little','maybe','i think so',
  ];

  return phrases.some((p) => normalized === p || normalized.startsWith(`${p} `));
}

function isCorrection(text = '') {
  const normalized = normalizeText(text);

  return [
    'actually','not ','no ','instead','i meant',
  ].some((p) => normalized.includes(p));
}

function isExpansion(text = '') {
  const normalized = normalizeText(text);

  return [
    'also','too','as well','and my','but also',
  ].some((p) => normalized.includes(p));
}

function getAnchorType({ pendingQuestion = null, lastAssistantMessage = '' } = {}) {
  if (pendingQuestion) return 'pending_question';
  if (isQuestion(lastAssistantMessage)) return 'prior_assistant_question';
  if (normalizeText(lastAssistantMessage)) return 'prior_assistant_statement';
  return 'none';
}

function getTurnAct({ message = '', anchorType = 'none' } = {}) {
  const normalized = normalizeText(message);

  if (!normalized) return 'unknown';

  if (isCorrection(normalized)) return 'correction';

  if (anchorType === 'pending_question' || anchorType === 'prior_assistant_question') {
    if (isQuestion(normalized)) return 'followup_question';
    return 'answer';
  }

  if (anchorType === 'prior_assistant_statement') {
    if (isQuestion(normalized)) return 'followup_question';
    if (isAgreement(normalized)) return 'agreement';
    if (isSoftClarification(normalized)) return 'clarification';
    if (isExpansion(normalized)) return 'expansion';
  }

  if (isQuestion(normalized)) return 'new_question';

  return 'new_statement';
}

function shouldAttachToPreviousTurn({ message = '', anchorType = 'none' } = {}) {
  const normalized = normalizeText(message);
  const count = wordCount(normalized);

  if (anchorType === 'none') return false;
  if (isAgreement(normalized)) return true;
  if (isSoftClarification(normalized)) return true;
  if (isCorrection(normalized)) return true;
  if (count <= 6 && containsPronoun(normalized)) return true;
  if (count <= 4) return true;

  return false;
}

function computeConfidence({ attached = false, anchorType = 'none', message = '' } = {}) {
  const normalized = normalizeText(message);

  if (!attached) return 0.35;
  if (anchorType === 'pending_question') return 0.95;
  if (isAgreement(normalized) || isSoftClarification(normalized)) return 0.9;
  if (containsPronoun(normalized) && wordCount(normalized) <= 6) return 0.85;
  if (anchorType === 'prior_assistant_statement') return 0.75;

  return 0.6;
}

/**
 * 🔥 NEW: use entityState instead of raw currentEntity
 */
function getActiveEntity(entityState = null) {
  if (!entityState) return null;
  return entityState.active || null;
}

function buildTurnAttachmentState({
  message = '',
  lastAssistantMessage = '',
  pendingQuestion = null,
  currentTopic = null,
  currentFrame = null,
  candidateEntity = null,
  candidateTopic = null,

  // 🔥 NEW INPUT
  entityState = null,
} = {}) {
  const anchorType = getAnchorType({
    pendingQuestion,
    lastAssistantMessage,
  });

  const attached = shouldAttachToPreviousTurn({
    message,
    anchorType,
  });

  const turnAct = getTurnAct({
    message,
    anchorType,
  });

  const activeEntity = getActiveEntity(entityState);

  const inheritEntity = attached && !!activeEntity && !candidateEntity;
  const inheritTopic = attached && !!currentTopic && !candidateTopic;
  const inheritFrame = attached && !!currentFrame;

  const resolvedEntity =
    candidateEntity || (inheritEntity ? activeEntity : null);

  const resolvedTopic =
    candidateTopic || (inheritTopic ? currentTopic : null);

  const confidence = computeConfidence({
    attached,
    anchorType,
    message,
  });

  return {
    attached,
    anchorType,
    turnAct,
    inheritEntity,
    inheritTopic,
    inheritFrame,
    resolvedEntity,
    resolvedTopic,
    confidence,
  };
}

export { buildTurnAttachmentState };